import { Module } from "../../ui/Module.js";
import { i18n } from "../../globalization/i18n.js";

export class AuthModule extends Module {

    static MODE_LOGIN = 10;
    static MODE_LOGOUT = 20;
    static MODE_LOGOUT_SUCCESS = 21;

    static ROUTE_LOGIN = 'auth-login';
    static ROUTE_LOGOUT = 'auth-logout';
    static ROUTE_LOGOUT_SUCCESS = 'auth-logout-success';

    constructor(app, options = {}) {

        super(app, options);

        this.moduleName = 'AuthModule';
        this.moduleTitle = null; // null => name of application in header

        this.viewMode = AuthModule.MODE_LOGIN;

        this.loginData = this.createLoginData();

    };

    // --------------------------------------------------------------------------------------------

    getLoginPageUrl() {

        return this.getApp().getRouteHash('auth-login');

    };

    getLogoutPageUrl() {

        return this.getApp().getRouteHash('auth-logout');

    };

    getRegisterPageUrl() {

        return this.getApp().getRouteHash('account-registration');

    };

    getResetpasswordPageUrl() {

        return this.getApp().getRouteHash('account-password-reset-start');

    };

    // --------------------------------------------------------------------------------------------

    createLoginData() {

        return {
            username: null,
            password: null,
        };

    };

    // --------------------------------------------------------------------------------------------

    _prepare(params, query) {

        return super._prepare(params);

    };

    _refresh(params) {

        let routeName = (params && params.route && params.route.name) || null;

        switch (routeName) {
            case 'auth-logout':
                this.viewMode = AuthModule.MODE_LOGOUT;
                this.invalidate();
                this.logoutUser();
                break;
            default:
                this.viewMode = AuthModule.MODE_LOGIN;
                break;
        };

        return super._refresh(params);

    };

    // --------------------------------------------------------------------------------------------

    isLoginDataValid() {

        let data = this.loginData || {};

        return (data.username && data.password);

    };

    // --------------------------------------------------------------------------------------------

    async loginUser() {

        const app = this.getApp();
        const authManager = this.getAuthManager();

        app.setBusyFlag();
        try {

            await authManager.login({ username: this.loginData.username, password: this.loginData.password });

        } catch (error) {

            const payload = error.payload ?? { code: 401000100, message: 'Unspecified authorization error.' };
            app.handleError(payload);

        } finally {

            app.clearBusyFlag();
            this.invalidate();

        }

    };

    async logoutUser() {

        const app = this.getApp();
        const authManager = this.getAuthManager();

        app.setBusyFlag();
        try {

            await authManager.logoutUser();
            this.viewMode = AuthModule.MODE_LOGOUT_SUCCESS;

        } catch (error) {

            this.viewMode = AuthModule.MODE_LOGOUT_SUCCESS;
            const payload = error.payload ?? { code: 401000100, message: 'Unspecified authorization error.' };
            app.handleError(payload);

        }
        finally {

            app.clearBusyFlag();
            this.invalidate();

        }

    };

    // --------------------------------------------------------------------------------------------

    _render(r, vm) {

        const module = this;

        let viewNode = null;

        switch (module.viewMode) {
            case AuthModule.MODE_LOGOUT:
                viewNode = renderLoggingOutView();
                break;
            case AuthModule.MODE_LOGOUT_SUCCESS:
                viewNode = renderLoggedOutView();
                break;
            default:
                viewNode = renderLogInView();
                break;
        }

        return r.module(null, normControls(viewNode));

        function normControls(controls) {
            if (Array.isArray(controls))
                return controls;
            return [controls];
        }

        function renderLogInView() {

            let resetPasswordUrl = module.getResetpasswordPageUrl(),
                registerUrl = module.getRegisterPageUrl();

            return [

                r.el('div', 'space'),

                r.card({ css: 'medium-width auto-horizontal-margin' }, [
                    r.header(null, [
                        r.h4([
                            i18n.t('module.auth.title_login')
                        ]),
                    ]),
                    r.section(null, [
                        r.field(null, [
                            r.label({ props: { for: 'ui-username' } }, [i18n.t('module.auth.lbl_username')]),
                            r.textBox(module.loginData.username, {
                                props: {
                                    id: 'ui-username',
                                    onkeyup: function (e) {
                                        module.loginData.username = e.target.value;
                                        module.invalidate();
                                    }
                                }
                            }),
                        ]),
                        r.field(null, [
                            r.label({ props: { for: 'ui-password' } }, [i18n.t('module.auth.lbl_password')]),
                            r.textBox(module.loginData.password, {
                                props: {
                                    id: 'ui-password',
                                    type: 'password',
                                    onkeyup: function (e) {
                                        module.loginData.password = e.target.value;
                                        module.invalidate();
                                    }
                                }
                            }),
                        ]),
                    ]),
                    r.footer(null, [
                        r.nav(null, [
                            r.div('max'),
                            r.textButton(i18n.t('module.auth.btn_login'), {
                                props: {
                                    type: 'button',
                                    disabled: !module.isLoginDataValid(),
                                    onclick: function (e) {
                                        module.loginUser();
                                    }
                                }
                            }),
                        ]),
                    ]),
                ]),

                r.section({ css: 'medium-width auto-horizontal-margin' }, [
                    r.div('s12', [
                        r.link({ url: resetPasswordUrl }, [
                            i18n.t('module.auth.lbl_reset_password'),
                        ]),
                    ]),
                    r.div('s12', [
                        r.link({ url: registerUrl }, [
                            i18n.t('module.auth.lbl_register'),
                        ]),
                    ]),
                ]),

            ];

        };

        function renderLoggingOutView() {

            return r.grid(null, [
                r.cell({ css: 's12' }, [
                    r.card(null, [
                        r.h4([
                            i18n.t('module.auth.title_logged_out'),
                        ]),
                        r.p([
                            r.icon('spinner'),
                            ' ',
                            i18n.t('module.auth.msg_logging_out'),
                        ])
                    ]),
                ]),
            ]);

        };

        function renderLoggedOutView() {

            let app = module.getApp(),
                loginUrl = app.getRouteHash('auth-login');

            return r.grid(null, [
                r.cell({ css: 's12' }, [
                    r.card(null, [
                        r.h4([
                            i18n.t('module.auth.title_logged_out'),
                        ]),
                        r.p([
                            i18n.t('module.auth.msg_logged_out'),
                        ]),
                    ]),
                ]),
                r.cell({ css: 's12' }, [
                    r.link({ url: loginUrl }, [
                        i18n.t('module.auth.lbl_back_to_login_page'),
                    ]),
                ])
            ]);

        };

    };

}